import { useGlobalState } from '../../../contexts/GlobalContext';
import { useIsMutating } from '@tanstack/react-query';
import { useGetLayouts } from '@vindr/services/actions/layout.action';
import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import { PaginationProps, SettingsContext } from '@vindr/services/interfaces';
import { addToArray, filterValidValue } from '@vindr/utils/helpers/common.helper';
import useControlModal from '@vindr/utils/hooks/useControlModal';
import { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { ImmerHook, useImmer } from 'use-immer';

const LayoutReportContext = createContext({});

export const INIT_FILTER_LAYOUT: Parameters<typeof useGetLayouts>[0] = {
  sort_field: 'updated_at',
  sort_order: SORT_ORDER.DESC,
};

export interface LayoutReportContext extends SettingsContext {
  layoutList: ReturnType<typeof useGetLayouts>['data']['data'];
  refetch: ReturnType<typeof useGetLayouts>['refetch'];
  editItem: LayoutReportContext['layoutList'][number];
  filter: Parameters<typeof useGetLayouts>[0];
  setFilter: ImmerHook<LayoutReportContext['filter']>[1];
}

const LayoutReportProvider = ({ children }: { children: React.ReactNode }) => {
  const [editItem, setEditItem] = useImmer<any>({});
  const [stateAction, setStateAction] = useState<'create' | 'edit'>('create');
  const { keycloak } = useGlobalState();

  const isUpdatingProfile = useIsMutating({
    mutationKey: [queryKey.update_profile],
  });
  const defaultInstitutionId = keycloak.userInfo?.default_institution_id;
  INIT_FILTER_LAYOUT.institution_ids = addToArray(defaultInstitutionId, undefined);

  const [filter, setFilter] = useImmer<Parameters<typeof useGetLayouts>[0]>({
    ...INIT_FILTER_LAYOUT,
  });

  useLayoutEffect(() => {
    if (isUpdatingProfile === 0) {
      INIT_FILTER_LAYOUT.institution_ids = addToArray(defaultInstitutionId, undefined);

      setFilter((draft) => {
        draft.institution_ids = addToArray(defaultInstitutionId, undefined);
      });
    }
  }, [defaultInstitutionId, isUpdatingProfile, setFilter]);

  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);

  const { openModal, setCloseModal, setOpenAddModal, setOpenEditModal } = useControlModal(
    setEditItem,
    setStateAction
  );

  const { data, isFetching, refetch } = useGetLayouts({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...filterValidValue(filter),
  });

  const { count: total, data: layoutList } = data || {};

  const memoizedValue = useMemo(
    () => ({
      editItem,
      filter,
      isLoading: isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      setStateAction,
      stateAction,
      layoutList,
      total,
    }),
    [
      editItem,
      filter,
      isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      setStateAction,
      stateAction,
      layoutList,
      total,
    ]
  );

  return (
    <LayoutReportContext.Provider value={memoizedValue} key={isUpdatingProfile}>
      {children}
    </LayoutReportContext.Provider>
  );
};
LayoutReportProvider.displayName = 'LayoutReportProvider';

export const useLayoutReportContext = () => {
  const context = useContext(LayoutReportContext);

  if (!context) {
    throw new Error('useLayoutReportContext must be used within a LayoutReportProvider');
  }

  return context as LayoutReportContext;
};

export default LayoutReportProvider;
